import { html, LitElement } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_OPTIONS_FORM } from '../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../../../../../../ui/gzDropdown';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../utils/htmlElement.utils';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../ui/gzChipsGeneratorInput';
import {
  AntimalwareFileTypesStatus,
  AntimalwareOnDemandScanningProfileType,
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskScanType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { FontSizes, FontWeights } from '../../../../../../../../../types/common/types';
import {
  getAntimalwareOnAccessScanningGeneralArchivesDepthFormOptions,
  getAntimalwareOnAccessScanningGeneralSettingsFileTypesStatusFormOptions,
  getAntimalwareOnDemandInfectedActionFormOptions,
  getAntimalwareOnDemandPuaActionFormOptions,
  getAntimalwareOnDemandRootkitActionFormOptions,
  getAntimalwareOnDemandScanTaskDefaultProfileDataSettings,
  MIN_ON_DEMAND_SCAN_TASK_ARCHIVE_SIZE,
} from '../../../../../../utils/uiSettings/antimalware/antimalware.values';
import { GzSelectionCardGroupWebComponent } from '../../../../../../../../../ui/gzSelectionCardGroup';
import { FormGroupValue } from '../../../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { getSupportLinkForPlatform } from '../../../../../../utils/links';
import { FormGroupChild } from '../../../../../../../../../ui/gzForm/formGroup/types/form-group-children';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_OPTIONS_FORM)
export class GzAntimalwareOnDemandScanTaskOptionsFormWebComponent extends LitElement {
  // options section
  @query('#scan-profile-field')
  private scanProfileField: GzFormFieldWebComponent<GzSelectionCardGroupWebComponent>;
  @query('#tasks-options-file-section')
  private scanSettingsFileSection: HTMLElement;
  @query('#scan-settings-file-types-field')
  private scanSettingsFileTypesField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#scan-settings-extensions-field')
  private scanSettingsExtensionsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#scan-settings-archives-enable-field')
  private scanSettingsArchivesEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#scan-settings-archives-settings-limit-size-field')
  private scanSettingsArchivesSettingsLimitSizeField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#scan-settings-archives-settings-maximum-depth-field')
  private scanSettingsArchivesSettingsMaximumDepthField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#scan-settings-archives-scan-email-field')
  private scanSettingsArchivesScanEmailField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#tasks-scan-settings-miscellaneous-scan-boot-field')
  private scanSettingsMiscellaneousScanBootField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-uefi-field')
  private scanSettingsMiscellaneousScanUefiField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-registry-field')
  private scanSettingsMiscellaneousScanRegistryField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-rootkits-field')
  private scanSettingsMiscellaneousScanRootkitsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-keyloggers-field')
  private scanSettingsMiscellaneousScanKeyloggersField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-network-files-field')
  private scanSettingsMiscellaneousScanNetworkFilesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-memory-field')
  private scanSettingsMiscellaneousScanMemoryField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-cookies-field')
  private scanSettingsMiscellaneousScanCookiesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-new-changed-field')
  private scanSettingsMiscellaneousScanNewChangedField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-scan-pua-field')
  private scanSettingsMiscellaneousScanPuaField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-resume-field')
  private scanSettingsMiscellaneousResumeField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-preserve-field')
  private scanSettingsMiscellaneousPreserveField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scan-settings-action-infected-field')
  private scanSettingsActionInfectedField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-scan-settings-action-rootkit-field')
  private scanSettingsActionRootkitField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-scan-settings-action-pua-field')
  private scanSettingsActionPuaField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  public formGroup: FormGroup;
  private value: AntimalwareOnDemandScanTask;
  private scanType = AntimalwareOnDemandScanTaskScanType.QUICK;
  private profileSettingsFormGroup!: FormGroup;
  private propsChangedFromInside = false;

  /**
   * Create form group and subscribe to valid.
   * @param changedProperties
   */
  protected firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.createFormGroup();
  }

  static get styles() {
    return styles;
  }

  createFormGroup(): void {
    this.scanSettingsActionRootkitField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.profileSettingsFormGroup = FormGroup.build({
      fileTypes: this.scanSettingsFileTypesField,
      extensions: this.scanSettingsExtensionsField,
      archives: {
        enable: this.scanSettingsArchivesEnableField,
        settings: {
          limitArchiveSize: this.scanSettingsArchivesSettingsLimitSizeField,
          maximumArchiveDepth: this.scanSettingsArchivesSettingsMaximumDepthField,
        },
        scanEmail: this.scanSettingsArchivesScanEmailField,
      },
      miscellaneous: {
        scanBoot: this.scanSettingsMiscellaneousScanBootField,
        scanUefi: this.scanSettingsMiscellaneousScanUefiField,
        scanRegistry: this.scanSettingsMiscellaneousScanRegistryField,
        scanRootkits: this.scanSettingsMiscellaneousScanRootkitsField,
        ignoreKeyloggers: this.scanSettingsMiscellaneousScanKeyloggersField,
        scanMemory: this.scanSettingsMiscellaneousScanMemoryField,
        scanCookie: this.scanSettingsMiscellaneousScanCookiesField,
        scanNewChanged: this.scanSettingsMiscellaneousScanNewChangedField,
        scanPUA: this.scanSettingsMiscellaneousScanPuaField,
        scanNetworkFiles: this.scanSettingsMiscellaneousScanNetworkFilesField,
        resumeIfInterrupted: this.scanSettingsMiscellaneousResumeField,
        preserveLastAccessTime: this.scanSettingsMiscellaneousPreserveField,
      },
      action: {
        whenInfected: {
          action: this.scanSettingsActionInfectedField,
        },
        whenRootKitAction: this.scanSettingsActionRootkitField,
        whenPua: {
          action: this.scanSettingsActionPuaField,
        }
      },
    });

    this.profileSettingsFormGroup.applyToFormGroupFields((formField: FormGroupChild) => {
      if (formField instanceof GzFormFieldWebComponent) {
        this.subscribeToFormField(formField);
      }
    });

    this.formGroup = FormGroup.build({
      scanProfile: this.scanProfileField,
      scanSettings: this.profileSettingsFormGroup,
    });
  }

  private subscribeToFormField(formField: GzFormFieldWebComponent): void {
    formField.value$.subscribe(() => {
      if (this.propsChangedFromInside) {
        return;
      }

      this.scanProfileField.value = AntimalwareOnDemandScanningProfileType.CUSTOM;
    });
  }

  public async setFormGroupValue(scanType, value) {
    await this.updateComplete;
    this.value = value;
    this.scanType = scanType;
    this.propsChangedFromInside = true;
    this.setDefaultViewForScanType();

    if (!this.value) {
      return;
    }

    this.formGroup.value = {
      scanProfile: this.value.scanProfile,
      scanSettings: {
        fileTypes: this.value.scanSettings.fileTypes,
        extensions: this.value.scanSettings.extensions,
        archives: this.value.scanSettings.archives,
        miscellaneous: this.value.scanSettings.miscellaneous,
        action: this.value.scanSettings.action,
      },
    };

    await this.updateComplete;
    this.propsChangedFromInside = false;
  }

  private setDefaultViewForScanType() {
    const isNetworkType = this.scanType === AntimalwareOnDemandScanTaskScanType.NETWORK;
    toggleVisibility(this.scanSettingsMiscellaneousScanBootField, !isNetworkType);
    toggleVisibility(this.scanSettingsMiscellaneousScanRegistryField, !isNetworkType);
    toggleVisibility(this.scanSettingsMiscellaneousScanRootkitsField, !isNetworkType);
    toggleVisibility(this.scanSettingsMiscellaneousScanMemoryField, !isNetworkType);
    toggleVisibility(this.scanSettingsMiscellaneousScanCookiesField, !isNetworkType);
    toggleVisibility(this.scanSettingsMiscellaneousScanNetworkFilesField, !isNetworkType);

    const isContextualType = this.scanType === AntimalwareOnDemandScanTaskScanType.CONTEXTUAL;
    toggleVisibility(this.scanSettingsFileSection, !isContextualType);
    this.scanSettingsFileTypesField.disabled = isContextualType;
    this.scanSettingsExtensionsField.disabled = isContextualType;

    const isDeviceType = this.scanType === AntimalwareOnDemandScanTaskScanType.DEVICE;

    toggleVisibility(this.scanSettingsMiscellaneousScanUefiField, !isNetworkType && !isContextualType && !isDeviceType);
    this.scanSettingsMiscellaneousScanUefiField.disabled = isContextualType || isDeviceType;

    toggleVisibility(this.scanSettingsMiscellaneousResumeField, !isContextualType && !isDeviceType);
    this.scanSettingsMiscellaneousResumeField.disabled = isContextualType || isDeviceType;

    toggleVisibility(this.scanSettingsMiscellaneousPreserveField, !isContextualType && !isDeviceType);
    this.scanSettingsMiscellaneousPreserveField.disabled = isContextualType || isDeviceType;
  }

  /**
   * Render specific content.
   */
  protected render() {
    const archivesHref = getSupportLinkForPlatform(
      { moduleId: ModuleId.ANTIMALWARE, sectionId: SectionId.ANTIMALWARE_ON_DEMAND },
      ['archiveInfo']
    );

    return html`
      <!-- Options - Scan options section -->
      <div id="tasks-options-scan-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Scan options')}</gz-title
        >

        <gz-form-field id="scan-profile-field" class="mt-3">
          <gz-selection-card-group gz-form-input @change="${this.onProfileTypeChange}">
            <gz-selection-card
              label="${translateFn('Aggressive')}"
              .value="${AntimalwareOnDemandScanningProfileType.AGGRESSIVE}"
            >
              <div slot="content">
                <gz-title
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.SEMIBOLD}"
                  style="margin-bottom: 8px"
                  >${translateFn('Advanced security, moderate use of resources')}</gz-title
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}">${'- ' + translateFn('Scan all files.')}</gz-text>
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Scan archives and emails.')}</gz-text
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Scan memory, boot sectors, registry.')}</gz-text
                >
              </div>
            </gz-selection-card>
            <gz-selection-card
              label="${translateFn('Normal')}"
              .value="${AntimalwareOnDemandScanningProfileType.NORMAL}"
            >
              <div slot="content">
                <gz-title
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.SEMIBOLD}"
                  style="margin-bottom: 8px"
                  >${translateFn('Standard security, low use of resources')}</gz-title
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Scan all new or modified files.')}</gz-text
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Scan archives and emails.')}</gz-text
                >
              </div>
            </gz-selection-card>
            <gz-selection-card
              label="${translateFn('Permissive')}"
              .value="${AntimalwareOnDemandScanningProfileType.PERMISSIVE}"
            >
              <div slot="content">
                <gz-title
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.SEMIBOLD}"
                  style="margin-bottom: 8px"
                  >${translateFn('Basic security, very low use of resources')}</gz-title
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Scan only new or modified application files.')}</gz-text
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}">${'- ' + translateFn('Scan emails.')}</gz-text>
              </div>
            </gz-selection-card>
            <gz-selection-card
              label="${translateFn('Custom')}"
              .value="${AntimalwareOnDemandScanningProfileType.CUSTOM}"
            >
              <div slot="content">
                <gz-title
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.SEMIBOLD}"
                  style="margin-bottom: 8px"
                  >${translateFn('Administrator-defined settings')}</gz-title
                >
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                  >${'- ' + translateFn('Configure your preferred scan options and locations.')}</gz-text
                >
              </div>
            </gz-selection-card>
          </gz-selection-card-group>
        </gz-form-field>
      </div>

      <!-- Options - File types section -->
      <div id="tasks-options-file-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('File types')}</gz-title
        >

        <gz-form-field class="mt-2" id="scan-settings-file-types-field" label="${translateFn('Scan')}">
          <gz-dropdown
            gz-form-input
            .options=${getAntimalwareOnAccessScanningGeneralSettingsFileTypesStatusFormOptions()}
            @selectionChanged="${this.handleGeneralLocalFileTypedChange}"
          ></gz-dropdown>
        </gz-form-field>

        <gz-form-field id="scan-settings-extensions-field">
          <gz-chips-generator-input
            gz-form-input
            hasFloatingLabel
            .strings=${{
              label: translateFn('Extensions'),
              placeholder: translateFn('Separate extensions by comma.'),
              hintText: translateFn('Each entry must be unique.'),
              notUniqueErrorMessage: translateFn('The entry already exists.'),
              clearListButtonText: translateFn('Clear list'),
              searchPlaceholder: translateFn('Search'),
              noSearchResultsString: translateFn('No results found'),
              deleteWindowTitle: translateFn('Delete extensions?'),
              deleteWindowContentText: translateFn(
                'You are about to delete all extensions from the list. This action cannot be undone.'
              ),
              cancelClearListBtnString: translateFn('Cancel'),
              clearListActionBtnString: translateFn('Delete'),
            }}
          >
          </gz-chips-generator-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <!-- Options - Archives section -->
      <div id="tasks-options-archives-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Archives')}</gz-title
        >

        <gz-form-field class="label-mw-250 checkbox-width mt-2" id="scan-settings-archives-enable-field">
          <gz-checkbox
            label="${translateFn('Scan inside archives')}"
            gz-form-input
            @change="${e => this.toggleGeneralArchiveDisabledState(!e.detail.checked)}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-text
          id="scan-settings-archives-enable-text"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="helper-text"
          .innerHTML=${translateFn(
            'Enable scanning in archives that are within the specified size and depth limits. Bitdefender engines can scan many types of archives that may contain threats. For the list of scanned archive types, refer to {link}this article{/link}.',
            {
              link: `<gz-link iconsize="20" type="underline" color="secondary" href="${archivesHref}"><span>`,
              '/link': '</span></gz-link>',
            }
          )}
        >
        </gz-text>

        <gz-form-field
          class="helper-text mt-2"
          id="scan-settings-archives-settings-limit-size-field"
          label="${translateFn('Limit archive size')}"
        >
          <gz-input-number
            gz-form-input
            inputWidth="${InputWidthValues.MEDIUM}"
            min="${MIN_ON_DEMAND_SCAN_TASK_ARCHIVE_SIZE}"
            unitSuffix="${translateFn('MB')}"
          ></gz-input-number>
        </gz-form-field>

        <gz-form-field
          class="helper-text"
          id="scan-settings-archives-settings-maximum-depth-field"
          label="${translateFn('Maximum archive depth (levels)')}"
        >
          <gz-dropdown
            gz-form-input
            .options=${getAntimalwareOnAccessScanningGeneralArchivesDepthFormOptions()}
            labelWidth=${InputWidthValues.MEDIUM}
          ></gz-dropdown>
        </gz-form-field>

        <gz-form-field class="label-mw-250 checkbox-width mt-2" id="scan-settings-archives-scan-email-field">
          <gz-checkbox label="${translateFn('Scan email archives')}" gz-form-input></gz-checkbox>
        </gz-form-field>
        <gz-text
          id="scan-settings-archives-scan-email-text"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="helper-text"
          .innerHTML=${translateFn(
            'Enable scanning of email message files and email databases, including file formats such as EML, MSG, PST, DBX, MBX, and TBB. Email archive scanning is resource-intensive and can affect system performance. For the list of scanned archive types, refer to {link}this article{/link}.',
            {
              link: `<gz-link iconsize="20" type="underline" color="secondary" href="${archivesHref}"><span>`,
              '/link': '</span></gz-link>',
            }
          )}
        >
        </gz-text>
      </div>

      <!-- Options - Miscellaneous section -->
      <div id="tasks-options-miscellaneous-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Miscellaneous')}</gz-title
        >

        <div class="container mt-2">
          <div class="objects">
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-boot-field">
              <gz-checkbox label="${translateFn('Scan boot sectors')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-uefi-field">
              <gz-checkbox label="${translateFn('Scan UEFI')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-registry-field">
              <gz-checkbox label="${translateFn('Scan registry')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-rootkits-field">
              <gz-checkbox
                label="${translateFn('Scan for rootkits')}"
                gz-form-input
                @change="${e => toggleVisibility(this.scanSettingsActionRootkitField, e.detail.checked)}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-keyloggers-field">
              <gz-checkbox label="${translateFn('Scan for keyloggers')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-network-files-field">
              <gz-checkbox label="${translateFn('Scan network shares')}" gz-form-input></gz-checkbox>
            </gz-form-field>
          </div>

          <div class="restrictions">
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-memory-field">
              <gz-checkbox label="${translateFn('Scan memory')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-cookies-field">
              <gz-checkbox label="${translateFn('Scan cookies')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-new-changed-field">
              <gz-checkbox label="${translateFn('Scan only new and changed files')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-scan-pua-field">
              <gz-checkbox
                label="${translateFn('Scan for potentially unwanted applications (PUA)')}"
                @change="${e => toggleVisibility(this.scanSettingsActionPuaField, e.detail.checked)}"
                gz-form-input
              ></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-resume-field">
              <gz-checkbox label="${translateFn('Resume scan after product update')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="tasks-scan-settings-miscellaneous-preserve-field">
              <gz-checkbox label="${translateFn('Preserve last access time')}" gz-form-input></gz-checkbox>
            </gz-form-field>
          </div>
        </div>
      </div>

      <!-- Options - Actions section -->
      <div id="rules-options-actions-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Actions')}</gz-title
        >
        <gz-form-field
          class="mt-2"
          id="tasks-scan-settings-action-infected-field"
          label="${translateFn('Default action for infected objects')}"
        >
          <gz-dropdown gz-form-input .options=${getAntimalwareOnDemandInfectedActionFormOptions()}></gz-dropdown>
        </gz-form-field>
        <gz-form-field
          id="tasks-scan-settings-action-rootkit-field"
          label="${translateFn('Default action for rootkits')}"
        >
          <gz-dropdown gz-form-input .options=${getAntimalwareOnDemandRootkitActionFormOptions()}></gz-dropdown>
        </gz-form-field>
        <gz-form-field
          id="tasks-scan-settings-action-pua-field"
          label="${translateFn('Default action for PUA')}"
        >
          <gz-dropdown gz-form-input .options=${getAntimalwareOnDemandPuaActionFormOptions()}></gz-dropdown>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Show/hide general local file extensions field.
   * Set validators for extensions field depending on the file type.
   * @param event
   * @private
   */
  private handleGeneralLocalFileTypedChange(event: CustomEvent) {
    const isVisible = event.detail.value === AntimalwareFileTypesStatus.USER_DEFINED_EXTENTIONS;
    toggleVisibility(this.scanSettingsExtensionsField, isVisible);

    this.scanSettingsExtensionsField.Validators = isVisible ? [RequiredValidator(translateFn('Required field'))] : [];
    this.scanSettingsExtensionsField.validate(true);
  }

  private toggleGeneralArchiveDisabledState(disabled: boolean) {
    toggleDisabled(this.scanSettingsArchivesSettingsLimitSizeField, disabled);
    toggleDisabled(this.scanSettingsArchivesSettingsMaximumDepthField, disabled);
  }

  /**
   * Change values of form fields according to the selected profile type.
   * @param event
   * @private
   */
  private async onProfileTypeChange(event: CustomEvent) {
    this.propsChangedFromInside = true;

    // skip if custom profile is selected
    const isCustomProfile = event.detail.value === AntimalwareOnDemandScanningProfileType.CUSTOM;
    if (isCustomProfile) {
      return;
    }

    // get default profile data settings
    const defaultProfileDataSettings = getAntimalwareOnDemandScanTaskDefaultProfileDataSettings(event.detail.value);
    if (defaultProfileDataSettings) {
      this.profileSettingsFormGroup.value = defaultProfileDataSettings as unknown as FormGroupValue;
      await this.updateComplete;
    }

    this.propsChangedFromInside = false;
  }
}
